<template>
  <v-row dense>
    <!-- It is desired to have duck curve for systemic and nodal but
    systemic data for duck curve isn't implemented yet -->
    <v-col v-if="selectedTab == 'nodal'" cols="12" sm="6">
      <Widget :title="`Duck Curve - ${nodeName}`">
        <template #append-title>
          <v-card flat width="120">
            <v-autocomplete
              v-model="duckCurveDate"
              outlined
              label="MM-DD"
              :items="dayItems"
              dense
              hide-details
            />
          </v-card>
        </template>
        <Plotly v-if="duckCurve.data.length" :data="duckCurve.data" :layout="duckCurve.layout" />
        <v-card v-else flat height="400" class="d-flex align-center justify-center">
          <v-card-text class="text-center text-subtitle-1">
            Time series results required for duck curve
          </v-card-text>
        </v-card>
      </Widget>
    </v-col>
    <v-col cols="12" sm="6" v-for="(chart, index) in charts" :key="`${index}${chart}`">
      <Widget :title="`${chart.title} - ${nodeName}`" height="100%">
        <template #append-title>
          <v-col class="pa-0">
            <v-select
              v-if="chart.title === 'Generation Mix' || chart.title === 'Generation Mix by Fuel'"
              v-model="generationMixYear"
              label="Year"
              :items="outputYears"
              item-text="title"
              item-value="value"
              width="100"
              hide-details
              outlined
              dense
            />
          </v-col>
        </template>
        <Plotly
          :data="chart.data"
          :layout="chartLayout(chart.layout)"
          :toImageButtonOptions="chartDownloadName(caseName, 'PC', chart.layout.title)"
        />
      </Widget>
    </v-col>
  </v-row>
</template>
<script>
import {
  camelToTitleCase,
  chartDownloadName,
  createAxis,
  filterLowChartValues,
  snakeToTitleCase,
} from "@/plugins/utils";
import deepmerge from "deepmerge";
import { DateTime } from "luxon";
import NPCDashboardMixin from "../../mixins/outputs/npc";

const commonLayout = {
  height: "400",
  margin: { pad: 0, t: 48, b: 64, l: 40, r: 40 },
  font: { size: 10 },
};

export default {
  mixins: [NPCDashboardMixin],
  name: "NodalPCDashboard",
  components: {
    Plotly: () => import("@/components/charts/PlotlyWrapper.vue"),
    Widget: () => import("@/components/visualizations/Widget.vue"),
  },
  props: {
    caseOutputs: { type: Object, required: false, default: () => ({}) },
    inputCase: { type: Object, required: false, default: () => ({}) },
    logoUrl: {
      type: String,
      required: false,
      default: "https://acelerex.com/wp-content/uploads/2021/01/rex-logo.da3e6933.png",
    },
    selectedNodeId: { type: Number, required: true, default: 1 },
    selectedTab: { type: String, required: true, default: "nodal" },
  },
  data() {
    const dayItems = Array(365)
      .fill(0)
      .map((_v, i) => ({
        text: DateTime.fromISO("2023-01-01").plus({ days: i }).toISODate().slice(5),
        value: i,
      }));
    return {
      generationMixYear: 0,
      outputYears: Object.keys(this.caseOutputs.yearlyResults).map((el, index) => ({
        title: el,
        value: index,
      })),
      caseName: this.inputCase.name,
      dayItems,
      duckCurveDate: 0,
    };
  },
  computed: {
    numYears() {
      const { year_start: startYear, year_end: endYear } = this.inputCase.settings;
      return endYear - startYear + 1;
    },
    nodeName() {
      return this.selectedTab === "nodal"
        ? this.inputCase.input.nodes.find((node) => node.id === this.selectedNodeId).name
        : "System";
    },
    yearlyOutputs() {
      if (this.selectedTab === "nodal") return this.yearlyOutputsByNode;
      return this.caseOutputs.yearlyResults;
    },
    yearlyOutputsByNode() {
      return Object.keys(this.caseOutputs.yearlyResults).reduce((acc, year) => {
        acc[year] = {
          ...Object.keys(this.caseOutputs.yearlyResults[year]).reduce((acc2, res) => {
            if (!acc2[res]) {
              // eslint-disable-next-line no-param-reassign
              acc2[res] = [];
            }
            acc2[res].push(
              ...this.caseOutputs.yearlyResults[year][res].filter(
                (el) => el.nodeID === this.selectedNodeId
              )
            );
            return acc2;
          }, {}),
        };
        return acc;
      }, {});
    },
    generatorTypes() {
      return this.allGeneratorOutputs(Object.keys(this.yearlyOutputs).slice(-1)).reduce(
        (types, generator) => {
          const fuelType = this.inputCase.input.generators.find(
            (gen) => gen.id === generator.id
          ).source;

          return { ...types, [generator.id]: fuelType };
        },
        {}
      );
    },
    generation() {
      const generationData = this.yearlyFuelTypeData("generation");
      return {
        title: "Generation",
        data: Object.entries(generationData)
          .map(([key, value]) => ({
            y: value,
            x: Object.keys(this.yearlyOutputs),
            name: snakeToTitleCase(key.toLowerCase()),
            stackgroup: "one",
            mode: "none",
          }))
          .filter(filterLowChartValues(0)),
        layout: {
          dragmode: "pan",
          visible: null,
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Generation (GWh)",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
        },
      };
    },

    generationMix() {
      const generators = this.getTotalMap(
        ["thermalGenerators", "hydroGenerators", "renewableGenerators"],
        "generation"
      );
      const values = generators
        .map((el) => el.value[this.generationMixYear])
        .filter((val) => val !== 0);
      return {
        data: [
          {
            values,
            labels: generators.map((el) => el.name),
            type: "pie",
            marker: { colors: generators.map((el) => this.$utils.getSeriesColor(el.resourceKey)) },
          },
        ],
        title: "Generation Mix",

        layout: {
          dragmode: "pan",
          images: [
            {
              xanchor: "right",
              xref: "paper",
              yanchor: "middle",
              yref: "paper",
              layer: "above",
              x: 1,
              y: 0.1,
              sizex: 0.24,
              sizey: 0.6,
              opacity: 0.8,
              source: this.logoUrl,
            },
          ],
        },
      };
    },

    generationMixByFuel() {
      const year = Object.keys(this.yearlyOutputs)[this.generationMixYear];
      const allGenerators = [
        ...this.yearlyOutputs[year].hydroGenerators,
        ...this.yearlyOutputs[year].renewableGenerators,
        ...this.yearlyOutputs[year].thermalGenerators,
      ];

      const res = allGenerators.reduce((acc, generator) => {
        const fuelType = snakeToTitleCase(
          this.inputCase.input.generators
            .find((inputGenerator) => inputGenerator.id === generator.id)
            .source.toLowerCase()
        );
        acc[fuelType] = (acc[fuelType] || 0) + generator.generation;
        return acc;
      }, {});

      return {
        title: "Generation Mix by Fuel",
        data: [
          {
            values: Object.values(res),
            labels: Object.keys(res),
            type: "pie",
            marker: { colors: Object.keys(res).map((el) => this.$utils.getSeriesColor(el)) },
          },
        ],
        layout: {
          dragmode: "pan",

          images: [
            {
              xanchor: "right",
              xref: "paper",
              yanchor: "middle",
              yref: "paper",
              layer: "above",
              x: 1,
              y: 0.1,
              sizex: 0.24,
              sizey: 0.6,
              opacity: 0.8,
              source: this.logoUrl,
            },
          ],
        },
      };
    },

    annualEnergy() {
      return {
        title: "Annual Peak Load and Annual Energy Demand",
        data: [
          {
            y: Object.values(this.yearlyOutputs).map((year) => {
              return year.demands.reduce((demandAcc, currDemand) => {
                return demandAcc + currDemand.peakLoad;
              }, 0);
            }, []),
            x: Object.keys(this.yearlyOutputs),
            mode: "scatter",
            name: "Peak Demand",
          },
          {
            y: Object.values(this.yearlyOutputs).map((year) => {
              return year.demands.reduce((demandAcc, currDemand) => {
                return demandAcc + currDemand.demand;
              }, 0);
            }, []),
            x: Object.keys(this.yearlyOutputs),
            mode: "scatter",
            name: "Energy Demand",
            yaxis: "y2",
          },
        ],
        layout: {
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MWh",
            },
            side: "right",
          },
          yaxis2: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
            overlaying: "y",
          },
          showlegend: true,
          legend: {
            orientation: "h",
          },
        },
      };
    },

    fuelPriceProjections() {
      const filterSources = ["SOLAR", "WIND", "HYDRO"];
      const generatorFuelPricesBySource = this.inputCase.input.generators.reduce((acc, val) => {
        if (filterSources.some((str) => val.source.includes(str))) return acc;
        acc[val.source] = [...(acc[val.source] || []), val.fuel_price];
        return acc;
      }, {});

      const fuelPricesObject = {};
      Object.keys(this.yearlyOutputs).forEach((currYear) => {
        const yearIdx = currYear - this.inputCase.settings.year_start;

        Object.entries(generatorFuelPricesBySource).forEach(([fuelType, fuelPrices]) => {
          const avgFuelPriceOfTypeForYear =
            fuelPrices.reduce((acc, val) => {
              return acc + val[yearIdx];
            }, 0) / generatorFuelPricesBySource[fuelType].length;
          fuelPricesObject[fuelType] = [
            ...(fuelPricesObject[fuelType] || []),
            avgFuelPriceOfTypeForYear,
          ];
        });
      });

      return {
        title: "Fuel Price Projections",
        data: Object.entries(fuelPricesObject).map(([key, value]) => {
          return {
            y: value,
            x: Object.keys(this.yearlyOutputs),
            name: snakeToTitleCase(key.toLowerCase()),
          };
        }),
        layout: {
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "$/MMBTU",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
        },
      };
    },

    generationCosts() {
      const keys = ["fuelCost", "vomCost", "fomCost", "emissionCost", "generationCost"];

      const dataSet = keys.map((dataKey) => {
        const yearlyData = Object.keys(this.yearlyOutputs).reduce((yearAcc, year) => {
          return [
            ...yearAcc,
            this.allGeneratorOutputs(year).reduce((genAcc, generator) => {
              return genAcc + generator[dataKey];
            }, 0),
          ];
        }, []);
        return {
          x: Object.keys(this.yearlyOutputs),
          y: yearlyData,
          name: camelToTitleCase(dataKey),
          type: dataKey === "generationCost" ? "line" : "bar",
        };
      });

      return {
        title: "Generation Costs",
        data: dataSet.filter(filterLowChartValues(0)),
        layout: {
          dragmode: "pan",
          visible: null,
          barmode: "relative",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Cost ($)",
            },
          },

          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
        },
      };
    },

    emission() {
      const keys = ["emission", "emissionCost"];
      const yearlyData = Object.keys(this.yearlyOutputs).reduce((yearAcc, year) => {
        Object.entries(
          this.allGeneratorOutputs(year).reduce((genAcc, generator) => {
            keys.forEach((key) => {
              genAcc[key] = (genAcc[key] || 0) + generator[key];
            });
            return genAcc;
          }, {})
        ).forEach(([key, val]) => {
          yearAcc[key] = [...(yearAcc[key] || []), val];
        });
        return yearAcc;
      }, {});

      return {
        title: "Emission",
        data: Object.entries(yearlyData)
          .map(([key, value], idx) => ({
            y: value,
            x: Object.keys(this.yearlyOutputs),
            name: camelToTitleCase(key),
            type: "line",
            yaxis: `y${idx + 1}`,
          }))
          .filter(filterLowChartValues(0)),
        layout: {
          dragmode: "pan",
          visible: null,
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          legend: { orientation: "h" },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Emission (ton)",
            },
          },
          yaxis2: {
            title: "Emission cost ($/ton)",
            overlaying: "y",
            side: "right",
          },
        },
      };
    },

    // Renewable Curtailment
    renewableEnergyCourtailment() {
      const yearlyData = Object.keys(this.yearlyOutputs).reduce((yearAcc, year) => {
        Object.entries(
          this.allGeneratorOutputs(year).reduce((genAcc, generator) => {
            const fuelType = this.inputCase.input.generators.find(
              (inputGenerator) => inputGenerator.id === generator.id
            ).source;
            genAcc[fuelType] = (genAcc[fuelType] || 0) + generator.energyCurtailment;
            return genAcc;
          }, {})
        ).forEach(([key, value]) => {
          yearAcc[key] = [...(yearAcc[key] || []), value];
        });
        return yearAcc;
      }, {});

      return {
        title: "Renewable Curtailment",

        data: Object.entries(yearlyData)
          .map(([key, value]) => ({
            y: value,
            x: Object.keys(this.yearlyOutputs),
            name: snakeToTitleCase(key.toLowerCase()),
            type: "bar",
          }))
          .filter(filterLowChartValues(0)),
        layout: {
          dragmode: "pan",
          visible: null,
          barmode: "stack",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MWh",
            },
          },
          showlegend: true,
          legend: { y: -0.15, orientation: "h" },
        },
      };
    },

    totalCost() {
      const totalCostData = this.yearlyFuelTypeData("totCost");

      return {
        title: "Total Cost",
        data: Object.entries(totalCostData).map(([fuelType, values]) => ({
          x: Object.keys(this.yearlyOutputs),
          y: values,
          name: snakeToTitleCase(fuelType.toLowerCase()),
          type: "bar",
        })),
        layout: {
          barmode: "relative",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "$/yr",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
        },
      };
    },
    storagePowerCapacity() {
      return {
        title: "Storage Power Capacity",
        data: Object.values(this.getStoragesWithField("assetCapacity")).map((storage) => {
          return {
            x: storage.x,
            y: storage.y,
            name: storage.name,
            type: "bar",
          };
        }),
        layout: {
          barmode: "relative",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
        },
      };
    },
    charts() {
      return [
        this.annualEnergy,
        this.totalCost,
        this.generation,
        this.renewableEnergyCourtailment,
        this.generationMix,
        this.generationMixByFuel,
        this.fuelPriceProjections,
        this.generationCosts,
        this.emission,
        this.storagePowerCapacity,
      ].filter((chart) => chart.data.length > 0);
    },
    duckCurve() {
      if (!this.caseOutputs.nodalDemands || !this.caseOutputs.nodalDemands.length) {
        return { data: [], layout: {} };
      }

      // duckCurveDate is 0-364, Luxon ordinals are 1-366.
      const selectedDayOfYear = this.duckCurveDate + 1;

      const dataByYear = this.caseOutputs.nodalDemands.reduce((acc, item) => {
        if (item.id !== this.selectedNodeId) {
          return acc;
        }

        const dt = DateTime.fromISO(item.Datetime, { zone: "UTC" });
        if (dt.ordinal !== selectedDayOfYear) {
          return acc;
        }

        const { year } = dt;
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push({ ...item, dt });
        return acc;
      }, {});

      const x = Array(24)
        .fill(0)
        .map((_v, i) => DateTime.fromISO("2023-01-01").plus({ hours: i }).toISO());

      const dataTraces = Object.entries(dataByYear).map(([year, values]) => {
        values.sort((a, b) => a.dt.hour - b.dt.hour);
        const netDemand = values.map((v) => v.demand - v.renewableGeneration);
        return {
          y: netDemand,
          x,
          name: year,
        };
      });

      return {
        data: dataTraces,
        layout: deepmerge(commonLayout, {
          yaxis: {
            rangemode: "tozero",
            title: "Net Demand (MW)",
          },
          xaxis: {
            tickformat: "%H:%M",
          },
          showlegend: true,
          legend: { orientation: "h" },
        }),
      };
    },
  },
  methods: {
    chartLayout(layout) {
      return deepmerge(commonLayout, layout);
    },

    allGeneratorOutputs(year) {
      if (!this.yearlyOutputs[year]) {
        return [];
      }
      return [
        ...this.yearlyOutputs[year].renewableGenerators,
        ...this.yearlyOutputs[year].hydroGenerators,
        ...this.yearlyOutputs[year].thermalGenerators,
      ];
    },

    getStoragesWithField(field) {
      const chartYears = Object.keys(this.yearlyOutputs);
      const calculatedStorages = {};

      Object.values(this.yearlyOutputs).forEach((year, yearIndex) => {
        year.storages.forEach((storage) => {
          if (calculatedStorages[storage.id] === undefined) {
            calculatedStorages[storage.id] = {
              x: chartYears,
              y: Array(chartYears.length).fill(0),
              name: storage.name,
            };
          }
          calculatedStorages[storage.id].y[yearIndex] = storage[field];
        });
      });

      return calculatedStorages;
    },

    yearlyFuelTypeData(key) {
      return Object.keys(this.yearlyOutputs).reduce((accYear, year) => {
        if (this.allGeneratorOutputs(year).length === 0) {
          [...new Set(Object.values(this.generatorTypes))].forEach((fuelType) => {
            accYear[fuelType] = [...(accYear[fuelType] || []), "-"];
          });
        } else {
          Object.entries(
            this.allGeneratorOutputs(year).reduce((accGenerators, generator) => {
              const fuelType = this.generatorTypes[generator.id]; // this.caseInputs.generators[generator.id].source
              return {
                ...accGenerators,
                [fuelType]: (accGenerators[fuelType] || 0) + generator[key],
              };
            }, {})
          ).forEach(([fuelType, totalDischarge], _) => {
            accYear[fuelType] = [...(accYear[fuelType] || []), totalDischarge];
          });
        }
        return accYear;
      }, {});
    },

    getTotalMap(source, key) {
      return source.reduce(
        (acc, el) => {
          acc.push(...this.mapChartData(el, key, true));
          return acc;
        },

        []
      );
    },
    filterLowChartValues,
    chartDownloadName,
    camelToTitleCase,
    createAxis,
    mapChartData(resource, key, total = false) {
      const chartData = Object.values(Object.values(this.yearlyOutputs)[0][resource]).map(
        (el, i) => ({
          id: el.id,
          name: el.name,
          value: Object.values(this.yearlyOutputs).map((res) => res[resource][i][key]) || [],
        })
      );

      if (!total) {
        return chartData;
      }
      if (!chartData[0]) return chartData;
      const value = new Array(chartData[0].value.length).fill(0);
      chartData.forEach((el, _) => {
        el.value.forEach((val, j) => {
          value[j] += val;
        });
      });
      return [
        {
          name: camelToTitleCase(resource),
          value,
          resourceKey: resource,
        },
      ];
    },
  },
};
</script>
